/**
 * Converts a matched bundle url into an identifier that survives the performance mark encoding, which joins
 * fields with "-" and would otherwise split hyphenated names such as "boot-critical-fluentui" into separate fields.
 * The content hash is kept so a stale bundle version is distinguishable from the current one.
 * @param bundleName - Name of the bundle as it appears in the asset url
 * @param contentHash - Content hash of the bundle as it appears in the asset url
 */ function toMarkerSafeBundleId(bundleName, contentHash) {
    return `${bundleName.replace(/-/g, "_")}.${contentHash}`;
}
/**
 * Collect cache hit/miss ratio metrics for static assets
 * @param bundlesToPrecache Bundle names configured for service worker precaching
 * @returns Record containing cache metrics, or null if Performance API is not supported or no resources are found
 */ export function collectScriptCacheMetrics(bundlesToPrecache, includePerBundleMetrics) {
    if (!performance?.getEntriesByType) {
        return null;
    }
    const resourceEntries = performance.getEntriesByType("resource");
    if (resourceEntries.length === 0) {
        return null;
    }
    let bundlePrecacheCacheHits = 0;
    let scriptCacheHits = 0;
    let otherCacheHits = 0;
    let totalTransferSize = 0;
    let totalEncodedSize = 0;
    let totalScripts = 0;
    let totalBundlePrecache = 0;
    let totalOther = 0;
    const cachedBundles = new Set();
    for (const entry of resourceEntries){
        const isScript = entry.initiatorType === "script";
        const fileName = entry.name.split("/").pop() ?? "";
        const bundleMatch = fileName.match(/^(.+)\.([a-f0-9]{10})\.(?:chunk|bundle)\.js$/);
        const bundleName = bundleMatch?.[1];
        const contentHash = bundleMatch?.[2];
        const isPrecachedBundle = bundleName !== undefined && bundlesToPrecache.includes(bundleName);
        if (isPrecachedBundle) {
            totalBundlePrecache++;
        } else if (isScript) {
            totalScripts++;
        } else {
            totalOther++;
        }
        if (entry.transferSize === 0) {
            if (isPrecachedBundle) {
                bundlePrecacheCacheHits++;
            } else if (isScript) {
                scriptCacheHits++;
            } else {
                otherCacheHits++;
            }
        }
        const hasUsableCacheTiming = entry.transferSize === 0 && entry.decodedBodySize > 0;
        if (isPrecachedBundle && contentHash && bundleName && hasUsableCacheTiming) {
            const bundleId = toMarkerSafeBundleId(bundleName, contentHash);
            cachedBundles.add(bundleId);
        }
        totalEncodedSize += entry.encodedBodySize || 0;
        totalTransferSize += entry.transferSize || 0;
    }
    const cacheMetrics = {
        totalScripts: totalScripts.toString(),
        totalBundlePrecache: totalBundlePrecache.toString(),
        totalOther: totalOther.toString(),
        precacheCacheHits: bundlePrecacheCacheHits.toString(),
        scriptCacheHits: scriptCacheHits.toString(),
        otherCacheHits: otherCacheHits.toString(),
        totalTransferSize: totalTransferSize.toString(),
        totalEncodedSize: totalEncodedSize.toString()
    };
    return includePerBundleMetrics ? {
        ...cacheMetrics,
        cachedBundles: Array.from(cachedBundles).join(",")
    } : cacheMetrics;
}
